import { SafeHtml } from '@angular/platform-browser';
import {
  GeometryDataInfo,
  GeometryLineStringData,
  GeometryPointData,
  GeometryPolygonData,
  LocalizedString,
  OpenDetailFromEnum,
  ShowOnFloorplanEnum,
  ShowOnFloorplanWhenActiveRouteEnum,
} from '@cue/api';

export interface NavigationNearestItem {
  name: LocalizedString;
  displayNameForApp?: LocalizedString;
  resourceId?: number;
  resourceGuid?: string;
  qrCodeId?: string;
  qrCategoryCodeId?: string;
  areaId: number;
  reserveable: boolean;
  breadcrumbWithIds: {
    item1: number;
    item2: LocalizedString;
  }[];
  icon?: string;
  file?: string | null;
  categoryIcon?: string;
  qrCodes: QrCodeData[];
  resourceTypeId: number;
  fileExtension?: string;
  imagePreferCover?: boolean;
  imageObjectPosition?: string;
  iconPreferCover?: boolean;
  iconObjectPosition?: string;
  resourceTypeName: LocalizedString;
  resourceDescription: LocalizedString;
  resourcePropertiesInfo: ResourcePropertyInfo[];
  searchResultIsShown: boolean;
  tags: TagData[];
}

export interface TagData {
  id: string;
  name: LocalizedString;
}

export interface ResourcePropertyInfo {
  valueNumber: number;
  valueDecimal: number;
  valueText: LocalizedString;
  valueHTMLContent: LocalizedString;
  name: LocalizedString;
  displayName: LocalizedString;
  searchableByName: boolean;
  searchableByValue: boolean;
}

export interface QrCodeData {
  qrCodeId: string;
  name: LocalizedString;
  mapName?: LocalizedString;
  qrCategoryCodeId?: string;
  showInStartList: boolean;
  showInTargetList: boolean;
  distance?: number;
  notNavigationPoint: boolean;
  qrCategoryName: string;
  tags: TagData[];
}

export interface NavigationBasicInfo {
  name: LocalizedString;
  displayNameForApp?: LocalizedString;
  resourceId?: number;
  resourceGuid?: string;
  reservable: boolean;
  location: string[];
  areaId: number;
  file?: string;
  icon: string | null;
  iconPreferCover: boolean | null;
  iconObjectPosition: string;
  imagePreferCover: boolean | null;
  imageObjectPosition: string;
  fileExtension?: string;
  distance?: number;
  qrCodes: QrCodeData[];
  qrCodeId?: string;
  resourceTypeId?: number;
  resourceTypeName: LocalizedString;
  resourceDescription: LocalizedString;
  resourcePropertiesInfo: ResourcePropertyInfo[];
  tags: TagData[];
}

export interface NavigationResourceBasicInfo extends NavigationBasicInfo {
  subject: string;
  organizer: string;
  start: Date;
  end: Date;
}

export enum ListType {
  StartList,
  TargetList,
}

export interface NavigationBasicInfoWithFirstLetter extends NavigationBasicInfo {
  letter: string;
  searchResultIsShown?: boolean;
}

export interface EventData {
  organizer: string;
  realStart: Date;
  realEnd: Date;
  resourceId: number;
  subject: string;
}

export interface NavigationResourceBasicInfoWithFirstLetter extends NavigationResourceBasicInfo {
  eventData: EventData[];
  reserveable: boolean;
}

export interface AlphabeticalGroupDataCrate {
  availableSymbolsList: string[];
  sortedDataList: NavigationBasicInfoWithFirstLetter[];
}

export enum StartTargetSelectionModeEnum {
  SelectedFromList = 0,
  ScanQR = 1,
  TypeQr = 2,
  SelectedFromDetail = 3,
  SelectedFromNearestList = 4,
  StartTargetSwitched = 5,
  ScanQRFromExternal = 6,
  Unchanged = 7,
  EmailLink = 8,
}

export interface FloorplanImageWithDimensions {
  imgSrc: string;
  width: number;
  height: number;
  poiFocus: number;
  referencePoints: NavigationReferencePoint[];
}

export interface NavigationReferencePoint {
  latitude: number;
  longitude: number;
  x: number;
  y: number;
}

//START NAVIGATION GEO DATA

export interface KioskBasicData {
  name: string;
  kioskId: string;
  rootAreaId: number;
  enable: boolean;
}

export type ZoomTarget =
  | {
      type: 'start';
      qrCodeId: string;
      areaId: number;
    }
  | {
      type: 'target';
      qrCodeId: string;
      areaId: number;
    }
  | {
      type: 'path';
    }
  | {
      type: 'map';
    };

export type MapPoiTypeDefinition = NavigationGeodataBM & {
  focused: boolean;
};

export interface NavigationGeodataBM {
  areaId: number;
  typeId: number;
  notNavigationPoint: boolean | null;
  resourceId?: number | null;
  childAreaId?: number | null;
  layerId: string;
  radius?: number;
  name?: LocalizedString | null;
  mapName?: LocalizedString;
  linkToImage?: string | null;
  linkToIcon?: string | null;
  isKiosk: boolean;
  qrCodeId?: string | null;
  showOnFloorplan?: ShowOnFloorplanEnum;
  showOnFloorplanDetail?: ShowOnFloorplanEnum;
  showOnFloorplanAboveIcon?: ShowOnFloorplanWhenActiveRouteEnum;
  geometry: GeometryPointData | GeometryLineStringData | GeometryPolygonData | GeometryDataInfo;
  qrCategoryColor?: string;
  zoomPoi: boolean;
  poiSize: number;
  redirectToResource: boolean;
  qrCode: string;
}

//END NAVIGATION GEO DATA

export enum ChangeAreaSourceEnum {
  Unknown = 'Unknown',
  ItemDetail = 'ItemDetail',
  Breadcrumb = 'Breadcrumb',
  MapItem = 'MapItem',
}

export interface RequestAreaChangeData {
  source: ChangeAreaSourceEnum;
  requestedAreaId: number;
  resourceName?: LocalizedString;
  startSelectionPriority: boolean;
  requestNewResource?: number;
  requestNewPOI?: string;
  possibleGroupIdxList?: number[];
}

export interface AreaUnavailableNotice {
  invalidAreaId: number;
}

export interface CurrentAreaInfo {
  areaId: number;
  currentAreaGroupIdx: number;
  stepDirection?: 'back' | 'forward';
}

export interface RepeatButtonIntervalData {
  itemId: string;
  startedIntervalId: number;
}

export interface INavigationResourceItem {
  areaId?: number;
  resourceId?: number;
  qrCodeId?: string;
  qrCategoryCodeId?: string;
  name?: LocalizedString | string;
  displayNameForApp?: LocalizedString | string;
  isResource?: boolean;
  startSelectionMode?: StartTargetSelectionModeEnum;
  targetSelectionMode?: StartTargetSelectionModeEnum;
}

export enum NavigationStateEnum {
  Default,
  StartSelection,
  TargetSelection,
  StepByStepNavigation,
  ResourcesDetail,
  ShowRouteMessage,
}

export enum RouteStateEnum {
  Unknown,
  NotFound,
  SameStartTarget,
  RequestRestartNavigation,
  StartNotFound,
  TargetNotFound,
  BarrierFreeRouteNotFound,
  InvalidKiosk,
  InvalidKioskLicence,
  KioskIsOutOfOrder,
}

export enum StepByStepNavigationStateEnum {
  NotStarted,
  StepByStepOn,
  StepByStepOff,
}

export interface INavigationData {
  start: INavigationResourceItem;
  target: INavigationResourceItem;
}

export enum AngleDirectionEnum {
  Straight,
  Left,
  Right,
  LowLeft,
  LowRight,
  SuperLowLeft,
  SuperLowRight,
  SuperLeft,
  SuperRight,
}

export interface GroupedInstructionItem {
  areaId: number;
  instructions: InstructionItem[];
  notShownOnMap?: boolean;
}

export interface InstructionItem {
  angleDirection: AngleDirectionEnum;
  distance: number;
  points: number[];
  areaId: number;
  nextAreaId?: number;
  isToNextArea: boolean;
  categoryIcon?: string;
  title?: LocalizedString | null;
  description?: LocalizedString | null;
}

export interface PortalDetail {
  parentPortalName: string;
  childPortalName: string;
  parentLayerId: string;
  childLayerId: string;
  parentAreaId: number;
  childAreaId: number;
  parentGeometry: string;
  childGeometry: string;
}

export interface PointInfo {
  x: number;
  y: number;
  areaId: number;
  nextAreaId: number;
  mapRatio: number;
  parentPortal?: PortalDetail;
  childPortal?: PortalDetail;
}

export enum AngleEnum {
  SuperLeft = 'SuperLeft',
  Left = 'Left',
  LowLeft = 'LowLeft',
  SuperLowLeft = 'SuperLowLeft',
  Straight = 'Straight',
  SuperLowRight = 'SuperLowRight',
  LowRight = 'LowRight',
  Right = 'Right',
  SuperRight = 'SuperRight',
}

export interface AreaMetric {
  areaId: number;
  floorplan: string;
  mapRatio: number;
  disableRotation?: boolean;
  name: string;
  breadcrumb: string[];
}

export interface ServerAliveResult {
  alive: boolean;
}

export interface StartTargetBasicData {
  x: number;
  y: number;
  poiSize: number;
  zoomPOI: boolean;
  areaId: number;
  icon: string | null;
  image: string | null;
  name: LocalizedString;
  showOnFloorplanAbove: ShowOnFloorplanWhenActiveRouteEnum;
}

export interface NavDirectionItem {
  index: number;
  skip: boolean;
  skipNext: number;
  second: PointInfo;
  first: PointInfo;
  nextAngle: AngleEnum;
  third: PointInfo;
  mapRatio: number;
  fiInfo: {
    fi: number;
    angle: AngleEnum;
    angleFi: number;
  };
  startPortalName?: string;
  endPortalName?: string;
  distance: number;
  startAreaId: number;
  endAreaId: number;
  newAreadId?: number;
  message: SafeHtml;
  areaMetric: AreaMetric;
  nextAngleType: AngleDirectionEnum;
  parentPortal: any;
  childPortal: any;
  distanceRenew: number;
  categoryName?: string;
  categoryIcon?: string;
  mapName?: string;
  isPortal: boolean;
  isLast: boolean;
  portalToPreviousAreaId?: number;
  isHideInNavigation: boolean;
  nextPointIsHideInNavigation: boolean;
  previousPointIsHideInNavigation: boolean;
  start: StartTargetBasicData;
  target: StartTargetBasicData;
  zoomPOI: boolean;
  poiSize: number;
}

export interface ResourcePoiIdentificationCrate {
  resourceId?: number;
  qrCodeId?: string;
  areaId?: number;
  openDetailFrom?: OpenDetailFromEnum;
}

export interface QrCodeDecodeResult {
  areaId: number;
  qrCodeId: string;
}
